// The renderer is the only validator for dynamic forms, so messages here are not ActiveModel's.

import {
  type DynamicFormField,
  type DynamicFormValue,
  type DynamicFormDefinition,
  type Option,
  type ValidationError,
  type ValidationErrors,
  isBooleanToggle,
  isGroupField,
  isRepeatableGroup,
} from './types'
import { type ResolutionLevel, isFieldVisible } from './visibility'
import { asObject } from './value'
import { richTextToText } from './richText'

const COLLECTION_TYPES = new Set(['multi_select', 'multi_checkbox', 'scalar_list', 'upload'])

export function validateDefinition(
  definition: DynamicFormDefinition,
  value: DynamicFormValue,
): ValidationErrors {
  const errors: ValidationErrors = {}
  const root: ResolutionLevel = { fields: definition.fields, value }
  validateFields(definition.fields, value, [root], '', errors)
  return errors
}

export function validateFieldAtPath(
  definition: DynamicFormDefinition,
  value: DynamicFormValue,
  path: string,
): ValidationErrors {
  const all = validateDefinition(definition, value)
  const scoped: ValidationErrors = {}
  for (const [key, errs] of Object.entries(all)) {
    if (key === path || key.startsWith(`${path}.`) || key.startsWith(`${path}[`)) {
      scoped[key] = errs
    }
  }
  return scoped
}

function validateFields(
  fields: DynamicFormField[],
  valueObj: Record<string, unknown> | undefined,
  levels: ResolutionLevel[],
  pathPrefix: string,
  errors: ValidationErrors,
): void {
  for (const field of fields) {
    if (!field.key) continue
    if (!isFieldVisible(field, levels)) continue

    const path = pathPrefix ? `${pathPrefix}.${field.key}` : field.key
    const fieldValue = valueObj?.[field.key]

    if (isGroupField(field)) {
      if (isRepeatableGroup(field)) {
        validateRepeatable(field, fieldValue, levels, path, errors)
      } else {
        const childObj = asObject(fieldValue)
        const childLevels: ResolutionLevel[] = [
          ...levels,
          { fields: field.children ?? [], value: childObj },
        ]
        validateFields(field.children ?? [], childObj, childLevels, path, errors)
      }
      continue
    }

    if (skipValidation(field)) continue
    validateScalar(field, fieldValue, path, errors)
  }
}

function validateRepeatable(
  field: DynamicFormField,
  value: unknown,
  levels: ResolutionLevel[],
  path: string,
  errors: ValidationErrors,
): void {
  const rows = Array.isArray(value) ? value : []
  const config = (isGroupField(field) ? field.config : undefined) ?? {}
  const min = config.repeat_min
  const max = config.repeat_max
  if (typeof min === 'number' && rows.length < min) {
    addError(errors, path, 'min_items', `must have at least ${min} ${plural(min, 'item')}`)
  }
  if (typeof max === 'number' && rows.length > max) {
    addError(errors, path, 'max_items', `must have at most ${max} ${plural(max, 'item')}`)
  }
  const children = isGroupField(field) ? (field.children ?? []) : []
  rows.forEach((row, i) => {
    const rowObj = asObject(row)
    const rowLevels: ResolutionLevel[] = [...levels, { fields: children, value: rowObj }]
    validateFields(children, rowObj, rowLevels, `${path}[${i}]`, errors)
  })
}

function skipValidation(field: DynamicFormField): boolean {
  return (
    field.type === 'markup' ||
    field.type === 'hidden' ||
    field.display_only === true ||
    field.hidden === true
  )
}

function validateScalar(
  field: DynamicFormField,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  if (field.required && isUnanswered(field, value)) {
    addError(errors, path, 'required', 'This field is required.')
    return
  }
  // Collection types still validate when empty: min_items applies without `required`.
  if (isUnanswered(field, value) && !COLLECTION_TYPES.has(field.type) && field.type !== 'boolean') return

  switch (field.type) {
    case 'text':
      return validateText(field.config, value, path, errors)
    case 'textarea':
      return validateTextarea(field.config, value, path, errors)
    case 'number':
      return validateNumber(field.config, value, path, errors)
    case 'date':
      return validateDate(field.config, value, path, errors)
    case 'radio':
    case 'select':
      return validateEnum(field.options, value, path, errors)
    case 'multi_select':
    case 'multi_checkbox':
      return validateMulti(field.config, field.options, value, path, errors)
    case 'scalar_list':
      return validateScalarList(field.config, value, path, errors)
    case 'upload':
      return validateUpload(field.config, value, path, errors)
    default:
      return
  }
}

function validateText(
  config: { min_length?: number; max_length?: number; pattern?: string } | undefined,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  const str = String(value)
  validateLength(config, str, path, errors)
  if (config?.pattern) {
    try {
      if (!new RegExp(config.pattern).test(str)) {
        addError(errors, path, 'pattern', 'is invalid')
      }
    } catch {
      // Malformed pattern in the definition — ignore rather than crash.
    }
  }
}

function validateTextarea(
  config:
    | {
        min_length?: number
        max_length?: number
        disallow_empty_spaces?: boolean
        disallow_only_empty_spaces?: boolean
      }
    | undefined,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  // Rich text values are HTML, so length checks count markup.
  const str = String(value)
  validateLength(config, str, path, errors)
  if (config?.disallow_only_empty_spaces && str.length > 0 && str.trim().length === 0) {
    addError(errors, path, 'blank', "can't be blank")
  }
  if (config?.disallow_empty_spaces && /\s/.test(str)) {
    addError(errors, path, 'no_spaces', 'must not contain spaces')
  }
}

function validateLength(
  config: { min_length?: number; max_length?: number } | undefined,
  str: string,
  path: string,
  errors: ValidationErrors,
): void {
  if (typeof config?.min_length === 'number' && str.length < config.min_length) {
    addError(
      errors,
      path,
      'min_length',
      `is too short (minimum is ${config.min_length} ${plural(config.min_length, 'character')})`,
    )
  }
  if (typeof config?.max_length === 'number' && str.length > config.max_length) {
    addError(
      errors,
      path,
      'max_length',
      `is too long (maximum is ${config.max_length} ${plural(config.max_length, 'character')})`,
    )
  }
}

function validateNumber(
  config:
    | { integer?: boolean; min?: number; max?: number; step?: number }
    | undefined,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  const num = typeof value === 'number' ? value : Number(String(value).trim())
  if (Number.isNaN(num) || (typeof value === 'string' && value.trim() === '')) {
    addError(errors, path, 'not_a_number', 'is not a number')
    return
  }
  if (config?.integer && !Number.isInteger(num)) {
    addError(errors, path, 'not_an_integer', 'must be an integer')
  }
  if (typeof config?.min === 'number' && num < config.min) {
    addError(errors, path, 'min', `must be greater than or equal to ${config.min}`)
  }
  if (typeof config?.max === 'number' && num > config.max) {
    addError(errors, path, 'max', `must be less than or equal to ${config.max}`)
  }
  if (typeof config?.step === 'number' && config.step > 0) {
    const base = typeof config.min === 'number' ? config.min : 0
    const ratio = (num - base) / config.step
    if (Math.abs(ratio - Math.round(ratio)) > 1e-9) {
      addError(errors, path, 'step', `must be a multiple of ${config.step}`)
    }
  }
}

function validateDate(
  config: { min?: string; max?: string } | undefined,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  const str = String(value)
  const time = Date.parse(str)
  if (Number.isNaN(time)) {
    addError(errors, path, 'invalid_date', 'is not a valid date')
    return
  }
  if (config?.min && time < Date.parse(config.min)) {
    addError(errors, path, 'min', `must be on or after ${config.min}`)
  }
  if (config?.max && time > Date.parse(config.max)) {
    addError(errors, path, 'max', `must be on or before ${config.max}`)
  }
}

function validateEnum(
  options: Option[] | undefined,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  if (!inlineOptions(options)) return // async data source — membership unknown
  if (!options!.some((opt) => valuesEqual(opt.value, value))) {
    addError(errors, path, 'inclusion', 'is not a valid option')
  }
}

function validateMulti(
  config: { min_items?: number; max_items?: number } | undefined,
  options: Option[] | undefined,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  const items = Array.isArray(value) ? value : []
  if (typeof config?.min_items === 'number' && items.length < config.min_items) {
    addError(errors, path, 'min_items', `must have at least ${config.min_items} ${plural(config.min_items, 'selection')}`)
  }
  if (typeof config?.max_items === 'number' && items.length > config.max_items) {
    addError(errors, path, 'max_items', `must have at most ${config.max_items} ${plural(config.max_items, 'selection')}`)
  }
  if (inlineOptions(options)) {
    const invalid = items.some((item) => !options!.some((opt) => valuesEqual(opt.value, item)))
    if (invalid) addError(errors, path, 'inclusion', 'contains an invalid option')
  }
}

function validateScalarList(
  config:
    | { item_type?: 'string' | 'integer' | 'number' | 'boolean'; min_items?: number; max_items?: number }
    | undefined,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  const items = Array.isArray(value) ? value : []
  if (typeof config?.min_items === 'number' && items.length < config.min_items) {
    addError(errors, path, 'min_items', `must have at least ${config.min_items} ${plural(config.min_items, 'item')}`)
  }
  if (typeof config?.max_items === 'number' && items.length > config.max_items) {
    addError(errors, path, 'max_items', `must have at most ${config.max_items} ${plural(config.max_items, 'item')}`)
  }
  const itemType = config?.item_type
  if (!itemType) return
  items.forEach((item, i) => {
    if (!coercesTo(item, itemType)) {
      addError(errors, `${path}[${i}]`, 'invalid_item', `must be a ${itemType}`)
    }
  })
}

function validateUpload(
  config:
    | { min_items?: number; max_items?: number; max_size?: number; accepted_types?: string[] }
    | undefined,
  value: unknown,
  path: string,
  errors: ValidationErrors,
): void {
  const files = Array.isArray(value) ? value : []
  if (typeof config?.min_items === 'number' && files.length < config.min_items) {
    addError(errors, path, 'min_items', `must have at least ${config.min_items} ${plural(config.min_items, 'file')}`)
  }
  if (typeof config?.max_items === 'number' && files.length > config.max_items) {
    addError(errors, path, 'max_items', `must have at most ${config.max_items} ${plural(config.max_items, 'file')}`)
  }
  files.forEach((file, i) => {
    if (file == null || typeof file !== 'object') return
    const node = file as { name?: string; size?: number }
    if (
      typeof config?.max_size === 'number' &&
      config.max_size > 0 &&
      typeof node.size === 'number' &&
      node.size > config.max_size
    ) {
      addError(errors, `${path}[${i}]`, 'max_size', 'file is too large')
    }
    if (config?.accepted_types?.length && node.name && !acceptsName(node.name, config.accepted_types)) {
      addError(errors, `${path}[${i}]`, 'accepted_type', 'file type is not allowed')
    }
  })
}

function addError(errors: ValidationErrors, path: string, code: string, message: string): void {
  const entry: ValidationError = { code, message }
  ;(errors[path] ??= []).push(entry)
}

/** `false` only means "not answered" on a consent checkbox; on a Yes/No pair,
 *  or an enum option carrying `false`, it is the answer. */
function isUnanswered(field: DynamicFormField, value: unknown): boolean {
  if (value === false) return field.type === 'boolean' && !isBooleanToggle(field)
  return isBlank(value)
}

/** Mirrors Ruby's `blank?`, `false` included. */
function isBlank(value: unknown): boolean {
  if (value == null || value === false) return true
  // Read as text: an emptied rich-text editor still holds `<p><br></p>`.
  if (typeof value === 'string') return richTextToText(value) === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value as object).length === 0
  return false
}

function inlineOptions(options: Option[] | undefined): boolean {
  return Array.isArray(options) && options.length > 0
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a === 'object' || typeof b === 'object') return false
  return String(a) === String(b)
}

function coercesTo(value: unknown, type: 'string' | 'integer' | 'number' | 'boolean'): boolean {
  switch (type) {
    case 'string':
      return typeof value === 'string'
    case 'boolean':
      return typeof value === 'boolean'
    case 'number':
      return typeof value === 'number' ? !Number.isNaN(value) : !Number.isNaN(Number(String(value).trim())) && String(value).trim() !== ''
    case 'integer': {
      const n = typeof value === 'number' ? value : Number(String(value).trim())
      return Number.isInteger(n) && String(value).trim() !== ''
    }
  }
}

function acceptsName(name: string, acceptedTypes: string[]): boolean {
  const lower = name.toLowerCase()
  return acceptedTypes.some((t) => {
    const ext = t.trim().toLowerCase().replace(/^\*?\.?/, '')
    return ext.length > 0 && lower.endsWith(`.${ext}`)
  })
}

function plural(count: number, word: string): string {
  return count === 1 ? word : `${word}s`
}
